import { trackPageView } from '@snowplow/browser-tracker';
import Cookies from 'js-cookie';
import stringToKebabCase from 'utils/textHelpers/stringToKebabCase';
import { AB_TEST_COOKIE_NAME } from '~/constants';

import {
  AppleSearchAdsInit,
  BookButtonClicked,
  BookingConfirmed,
  BusinessEvent,
  BusinessEventHandler,
  CheckoutPageViewed,
  Click,
  ContactHostFormSent,
  ContactHostFormViewed,
  ExperiencePageViewed,
  ExperienceReviewUpvoteChange,
  FAQViewed,
  LocalPickerSubmitted,
  PageView,
  ReadMoreClicked,
  SearchPageViewed,
} from '../../businessLayer';

function addCommonContext(customEventType: string, evt: BusinessEvent) {
  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });
  return {
    schema: 'iglu:com.withlocals.ue/common_data/jsonschema/1-0-1',
    data: {
      custom_event_type: customEventType,
      traffic_type: evt.traffic_type,
      account_is_host: evt.account_is_host,
      account_id: evt.account_id,
      application: 'ue',
      storefront_ab_test: Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test',
      ...experimentEventData,
    },
  };
}

const snowplowHandler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = {
      page_title: pageView.title,
      page_location: pageView.location,
      page_path: pageView.path,
    };

    console.log('[SNOWPLOW] sending page view:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('page_view', evt),
      ],
    });
  }

  if (evt.type === 'AppleSearchAdsInit') {
    const event = evt as AppleSearchAdsInit;
    const eventData = {
      ...event.data,
    };

    console.log('[SNOWPLOW] sending apple search ads init:', eventData);

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/apple_search_ads_init/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('apple_search_ads_init', evt),
      ],
    });
  }

  if (evt.type === 'FAQViewed') {
    const event = evt as FAQViewed;
    const eventData = {
      experience_id: event?.experience?.id,
    };

    console.log('[SNOWPLOW] sending FAQ viewed:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/faq_viewed/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('faq_viewed', evt),
      ],
    });
  }

  if (evt.type === 'SearchPageViewed') {
    const event = evt as SearchPageViewed;
    const eventData = {
      area: event.city,
      category: event.category,
    };

    console.log('[SNOWPLOW] sending search page view:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view_search/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('page_view_search', evt),
      ],
    });
  }

  if (evt.type === 'ContactOtherHostsClick') {
    const eventData = {};
    console.log('[SNOWPLOW] sending contact_other_hosts_clicked:', eventData);
    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/contact_other_hosts_clicked/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('contact_other_hosts_clicked', evt),
      ],
    });
  }

  if (evt.type === 'ExperiencePageViewed') {
    const event = evt as ExperiencePageViewed;
    const eventData = {
      experience: event.experience?.id,
      country: event.experience?.country,
      area: event.experience?.area,
    };

    console.log('[SNOWPLOW] sending experience page view:', eventData);

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/experience_page_viewed/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('experience_page_viewed', evt),
      ],
    });
  }

  if (evt.type === 'BookButtonClicked') {
    const event = evt as BookButtonClicked;
    const eventData = {
      experience_id: event?.experience?.id,
    };

    console.log('[SNOWPLOW] sending book button clicked:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/book_button_clicked/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('book_button_clicked', evt),
      ],
    });
  }

  if (evt.type === 'ReadMoreClicked') {
    const event = evt as ReadMoreClicked;
    const eventData = {
      experience_id: event?.experience?.id,
    };

    console.log('[SNOWPLOW] sending read more clicked:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/read_more_clicked/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('read_more_clicked', evt),
      ],
    });
  }

  if (evt.type === 'Click') {
    const event = evt as Click;
    const eventData: { [key: string]: any } = {};
    if (event?.buttonText) eventData.buttonText = event.buttonText;
    if (event?.action) eventData.action = event.action;
    if (event?.experience_id) eventData.experience_id = event.experience_id;
    if (event?.city) eventData.city = event.city;
    if (event?.rating) eventData.rating = event.rating;
    if (event?.time) eventData.time = event.time;
    if (event?.date) eventData.date = event.date;
    if (event?.host?.id) eventData.host_id = event.host.id;
    if (event?.languages) eventData.languages = event.languages;
    if (event?.nrOfAdults) eventData.nr_of_adults = event.nrOfAdults;
    if (event?.nrOfChildren) eventData.nr_of_children = event.nrOfChildren;
    if (event?.keywords) eventData.keywords = event.keywords;
    if (event?.categories) eventData.categories = event.categories;
    if (event?.subCategories) eventData.sub_categories = event.subCategories;
    if (event?.transportMode) eventData.transport_mode = event.transportMode;
    if (event?.price) eventData.price = event.price;
    if (event?.durationFilter) eventData.duration_filter = event.durationFilter;
    if (event?.familyFriendly) eventData.family_friendly = event.familyFriendly;
    if (event?.instantConfirmation)
      eventData.instant_confirmation = event.instantConfirmation;

    console.log('[SNOWPLOW] sending click:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/click/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('click', evt),
      ],
    });
  }

  if (evt.type === 'BookingConfirmed') {
    const event = evt as BookingConfirmed;
    const eventData = event.getEventData();

    console.log('[SNOWPLOW] sending booking confirmed:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/checkout_completed/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('checkout_completed', evt),
      ],
    });
  }

  if (evt.type === 'CheckoutPageViewed') {
    const event = evt as CheckoutPageViewed;
    const eventData = {
      experience: event?.trip?.experience_id,
      trip_id: event?.trip?.id,
      area: event?.trip?.city,
      country: event?.trip?.country,
    };

    console.log('[SNOWPLOW] sending checkout page view:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view_checkout/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('page_view_checkout', evt),
      ],
    });
  }

  if (evt.type === 'ContactHostFormViewed') {
    const event = evt as ContactHostFormViewed;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = {
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      area: stringToKebabCase(hostLocation?.area),
    };

    console.log('[SNOWPLOW] sending contact host form viewed:', eventData);

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/page_view_contact_host_form/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('page_view_contact_host_form', evt),
      ],
    });
  }

  if (evt.type === 'ExperienceReviewUpvoteChange') {
    const event = evt as ExperienceReviewUpvoteChange;
    const eventData = {
      experience_id: event?.experience_id,
      review_id: event?.review_id,
      action: event?.action,
    };
    console.log(
      '[SNOWPLOW] sending experience review upvote change:',
      eventData,
    );

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/experience_review_upvote_change/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('experience_review_upvote_change', evt),
      ],
    });
  }

  if (evt.type === 'ContactHostFormSent') {
    const event = evt as ContactHostFormSent;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = {
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      area: stringToKebabCase(hostLocation?.area),
      nrOfAdults: event?.nrOfAdults,
      nrOfChildren: event?.nrOfChildren,
      date: event?.date,
      time: event?.time,
      duration: event?.duration,
    };

    console.log('[SNOWPLOW] sending contact host form sent:', eventData);

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/contact_host_form_sent/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('contact_host_form_sent', evt),
      ],
    });
  }

  if (evt.type === 'LocalPickerSubmitted') {
    const event = evt as LocalPickerSubmitted;
    const eventData = {
      experience_id: event.experience_id,
      selected: event.selected,
      options: event.options,
    };

    console.log('[SNOWPLOW] sending local picker submitted:', eventData);

    return trackPageView({
      context: [
        {
          schema:
            'iglu:com.withlocals.ue/local_picker_submitted/jsonschema/1-0-0',
          data: eventData,
        },
        addCommonContext('local_picker_submitted', evt),
      ],
    });
  }

  console.warn('[SNOWPLOW] received non-implemented event:', evt);
};

export { snowplowHandler };
